import React, { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { QueryKey, queryCache, useQuery } from "react-query";
import { useTranslation } from "react-i18next";

import {
  BusinessRole,
  Company,
  PermissionLevel,
} from "fe-shared/src/types/domain";
import { IconButton } from "fe-shared/src/components";
import { EditIcon } from "fe-shared/src/components/Icons";
import { hasPermission } from "fe-shared/src/helpers/permissions";
import useDialog from "fe-shared/src/hooks/useDialog";
import { ApiError } from "fe-shared/src/types/common";
import {
  EditAssignmentDialog,
  PickupRequestDetails,
  PickupRequestDialog,
  PickupRequestSendButton,
} from "fe-shared-requests/src/components";
import {
  PickupRequestDetails as PickupRequestDetailsModel,
  PickupRequestStatus,
} from "fe-shared-requests/src/types";
import {
  canEditAssignment,
  canResendPickupRequest,
  canSendPickupRequest,
  isPickupRequestEditable,
} from "fe-shared-requests/src/helpers/pickupRequestStatus";
import {
  getDocuments,
  getRequest,
} from "fe-shared-requests/src/api/pickupRequests";

import UserContext from "contexts/userContext";
import RequestStatusHeader from "components/RequestStatusHeader";
import RequestLinkedTransaction from "components/RequestLinkedTransaction";
import PickupRequestInvalidateButton from "components/PickupRequestInvalidateButton";
import {
  PERMISSIONS,
  REQUEST_ACTIVITIES_QUERY_KEY,
  REQUEST_DETAILS_QUERY_KEY,
  REQUEST_DOCUMENTS_QUERY_KEY,
  REQUESTS_QUERY_KEY,
} from "consts";

const isEditableByRole = (
  isHauler: boolean,
  status?: PickupRequestStatus
): boolean =>
  !!status &&
  isPickupRequestEditable(status, false) &&
  !(isHauler && status === PickupRequestStatus.Submitted);

const INVALIDATABLE_STATUSES: PickupRequestStatus[] = [
  PickupRequestStatus.Approved,
  PickupRequestStatus.Requested,
  PickupRequestStatus.Accepted,
];

interface Props {
  company: Company;
  returnUrl: string;
}

const RequestDetails = ({
  company: { permissions, businessRoleCode },
  returnUrl,
}: Props) => {
  const { requestNumber } = useParams<{ requestNumber: string }>();
  const { t } = useTranslation();
  const currentUser = useContext(UserContext);
  const isHauler = businessRoleCode === BusinessRole.Hauler;

  const canViewRequest = hasPermission(permissions, {
    name: PERMISSIONS.viewRequest,
    level: PermissionLevel.Read,
  });
  const canViewDocuments = hasPermission(permissions, {
    name: PERMISSIONS.requestDocuments,
    level: PermissionLevel.Read,
  });
  const canManageDocuments = hasPermission(permissions, {
    name: PERMISSIONS.requestDocuments,
    level: PermissionLevel.Full,
  });
  const canViewHistory = hasPermission(permissions, {
    name: PERMISSIONS.requestHistory,
    level: PermissionLevel.Read,
  });

  const canEdit = hasPermission(permissions, {
    name: PERMISSIONS.requestEdit,
    level: PermissionLevel.Full,
  });

  const canInvalidate = hasPermission(permissions, {
    name: PERMISSIONS.requestInvalidate,
    level: PermissionLevel.Full,
  });

  const canSend = hasPermission(permissions, {
    name: PERMISSIONS.requestSend,
    level: PermissionLevel.Full,
  });

  const canResend = hasPermission(permissions, {
    name: PERMISSIONS.requestResend,
    level: PermissionLevel.Full,
  });

  const canOpenTransaction = hasPermission(permissions, {
    name: PERMISSIONS.transactionDetails,
    level: PermissionLevel.Read,
  });

  const requestQueryKey: QueryKey = [REQUEST_DETAILS_QUERY_KEY, requestNumber];
  const documentsQueryKey: QueryKey = [
    REQUEST_DOCUMENTS_QUERY_KEY,
    requestNumber,
  ];
  const request = useQuery<PickupRequestDetailsModel, AxiosError<ApiError>>(
    requestQueryKey,
    () => getRequest(requestNumber),
    { enabled: canViewRequest }
  );

  const documentsQuery = useQuery(
    documentsQueryKey,
    () => getDocuments(requestNumber),
    { enabled: !!request.data && canViewDocuments }
  );

  const {
    isDialogActive: isEditDialogActive,
    isDialogRendered: isEditDialogRendered,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog,
  } = useDialog();
  const [editDialogInstanceKey, setEditDialogInstanceKey] = useState(0);

  const onEditButtonClicked = () => {
    setEditDialogInstanceKey((key) => key + 1);
    openEditDialog();
  };

  const {
    isDialogRendered: isAssignmentDialogRendered,
    openDialog: openAssignmentDialog,
    closeDialog: closeAssignmentDialog,
  } = useDialog();

  const canEditAssignmentNow =
    isHauler &&
    canEdit &&
    !!request.data &&
    canEditAssignment(request.data.status, "hauler");

  const canEditNow =
    canEdit && isEditableByRole(isHauler, request.data?.status);

  const canSendNow =
    isHauler && canSend && canSendPickupRequest(request.data);
  const canResendNow =
    isHauler && canResend && canResendPickupRequest(request.data);
  const canInvalidateNow =
    canInvalidate &&
    !!request.data &&
    INVALIDATABLE_STATUSES.includes(request.data.status);

  return canViewRequest ? (
    <PickupRequestDetails
      returnUrl={returnUrl}
      requestNumber={requestNumber}
      request={request}
      currentUserEmail={currentUser?.email || ""}
      canViewPriority={false}
      canViewFlag
      canViewCarrier
      canViewReasons={isHauler}
      canViewDocuments={canViewDocuments}
      canManageDocuments={canManageDocuments}
      canViewHistory={canViewHistory}
      canExportHistory={false}
      canViewNotes={false}
      assigneeSlot={null}
      assignmentActionSlot={
        canEditAssignmentNow && (
          <>
            <IconButton
              hasTooltip
              onClick={openAssignmentDialog}
              id="request-assignment-edit-button"
              aria-label={t("pickupRequests.assignment.edit")}
            >
              <EditIcon fillColor={null} />
            </IconButton>
            {isAssignmentDialogRendered && request.data && (
              <EditAssignmentDialog
                request={request.data}
                callerRole="hauler"
                onClose={closeAssignmentDialog}
                onChanged={() => {
                  queryCache.invalidateQueries(requestQueryKey);
                  queryCache.invalidateQueries(REQUESTS_QUERY_KEY);
                }}
                onSaved={() => {
                  queryCache.invalidateQueries(requestQueryKey);
                  queryCache.invalidateQueries(REQUESTS_QUERY_KEY);
                  closeAssignmentDialog();
                }}
              />
            )}
          </>
        )
      }
      headerActionsSlot={
        canEditNow && (
          <>
            <IconButton
              hasTooltip
              onClick={onEditButtonClicked}
              id="request-edit-button"
              aria-label={t("pickupRequests.details.edit")}
            >
              <EditIcon fillColor={null} />
            </IconButton>
            {isEditDialogRendered && (
              <PickupRequestDialog
                key={editDialogInstanceKey}
                mode="edit"
                request={request.data}
                requestNumber={request.data?.requestNumber ?? requestNumber}
                existingDocuments={documentsQuery.data ?? []}
                canManageDocuments={canManageDocuments}
                canDeleteDocuments={false}
                showPortalOnlyFields={false}
                showReasons={isHauler}
                requestsQueryKey={REQUESTS_QUERY_KEY}
                isActive={isEditDialogActive}
                onClose={closeEditDialog}
                onSuccess={() => {
                  queryCache.invalidateQueries(requestQueryKey);
                  queryCache.invalidateQueries([
                    REQUEST_ACTIVITIES_QUERY_KEY,
                    requestNumber,
                  ]);
                  queryCache.invalidateQueries(documentsQueryKey);
                }}
              />
            )}
          </>
        )
      }
      actionsSlot={
        (canSendNow || canResendNow || canInvalidateNow) && (
          <>
            {(canSendNow || canResendNow) && (
              <PickupRequestSendButton
                isResend={canResendNow && !canSendNow}
                requestNumber={request.data?.requestNumber ?? requestNumber}
                onSuccess={() => {
                  queryCache.invalidateQueries(requestQueryKey);
                  queryCache.invalidateQueries([
                    REQUEST_ACTIVITIES_QUERY_KEY,
                    requestNumber,
                  ]);
                  queryCache.invalidateQueries(REQUESTS_QUERY_KEY);
                }}
              />
            )}
            {canInvalidateNow && (
              <PickupRequestInvalidateButton
                requestNumber={request.data?.requestNumber ?? requestNumber}
              />
            )}
          </>
        )
      }
      titleSlot={
        request.data && (
          <>
            <RequestStatusHeader
              requestNumber={requestNumber}
              status={request.data.status}
            />
            <RequestLinkedTransaction
              transactionNumber={request.data.linkedTransactionNumber}
              isHauler={isHauler}
              canOpenTransaction={canOpenTransaction}
            />
          </>
        )
      }
    />
  ) : null;
};

export default RequestDetails;
